import clsx from 'clsx';
import {
  FocusEventHandler,
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  KeyboardEventHandler,
  MouseEventHandler,
} from 'react';
import {
  Control,
  FieldError,
  FieldPath,
  FieldValues,
  get,
  RegisterOptions,
  UseFormRegisterReturn,
  useFormState,
} from 'react-hook-form';

import FormError from '../FormError';
import FormLabel from '../FormLabel';

interface Props {
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  type?: HTMLInputTypeAttribute;
  register?: UseFormRegisterReturn;
  onChange?: (value: string, name?: string) => void;
  onClick?: MouseEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  pattern?: string;
  value?: string;
  min?: number;
  max?: number;
  maxLength?: number;
  autoComplete?: string;
}

const TextInput = function TextInput({
  className,
  disabled,
  placeholder,
  name,
  type = 'text',
  onChange,
  onFocus,
  onBlur,
  onClick,
  onKeyDown,
  value,
  register,
  pattern,
  max,
  min,
  maxLength,
  autoComplete,
}: Props): JSX.Element {
  const inputProps: InputHTMLAttributes<HTMLInputElement> = {
    name,
    placeholder,
    disabled,
    type,
    onChange: onChange && ((e) => onChange(e.target.value, e.target.name)),
    onFocus,
    onBlur,
    onClick,
    onKeyDown,
    value,
    pattern,
    max,
    min,
    maxLength,
    autoComplete,
    ...register,
  };
  return (
    <input
      className={clsx(
        'block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm',
        className,
      )}
      {...inputProps}
    />
  );
};

interface TextInputLabelledProps extends Props {
  label?: React.ReactNode;
  sublabel?: React.ReactNode;
  error?: React.ReactNode;
}

TextInput.Labelled = function TextInputLabelled({
  label,
  className,
  error,
  sublabel,
  ...rest
}: TextInputLabelledProps): JSX.Element {
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <label className={clsx('block', className)}>
      {label && <FormLabel>{label}</FormLabel>}
      <TextInput {...rest} />
      {error && <FormError>{error}</FormError>}
      {sublabel && <span className="subtext">{sublabel}</span>}
    </label>
  );
};

interface TextInputControllerProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends TextInputLabelledProps {
  control: Control<TFieldValues>;
  name: TFieldName;
  registerOptions?: RegisterOptions<TFieldValues, TFieldName>;
}

TextInput.LabelledController = function TextInputController<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  registerOptions,
  ...rest
}: TextInputControllerProps<TFieldValues, TFieldName>): JSX.Element {
  const { errors } = useFormState({ control, name });
  const error = get(errors, name) as FieldError | undefined;

  return (
    <TextInput.Labelled
      register={control.register(name, registerOptions)}
      error={error?.message}
      {...rest}
    />
  );
};

export default TextInput;
